import React from 'react';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import { Box, Typography, Divider, IconButton } from '@material-ui/core';
import {
  OutageMarkerView,
  useOutageLeftPanelState,
} from '../../providers/OutageLeftPanelStateProvider';
import HomePinIcon from '../icons/HomePinIcon';
import CancelIcon from '@material-ui/icons/Cancel';
import OutageMarkerIcon from '../icons/OutageMarkerIcon';
import CRCIcon from '../icons/CRCIcon';
import { useTranslation } from '../../../../hooks/useTranslation';
import colors from '../../../../themes/main-colors';
import { PSPSInfo } from '../psps-info';
import clsx from 'clsx';
import { ServicePointOutageDetails } from '../../../../__generated__/pge-types';
import { RestorationStatusFAQ } from '../restoration-status-faq';
import { toDateString } from '../../../../util/format';
import { useOutageMapContext } from '../../providers/OutageMapProvider';
import useAuth from '../../../../hooks/useAuth';
import { ChevronLeft } from '@material-ui/icons';
import { useIsMobile } from '../../../../util/style-utils';
import { getRestorationStatus } from '../../utils/utils';
import PlannedOutageIcon from '../icons/PlannedOutageIcon';
import { ResetCRCLeftPanel } from '../community-resource-center/ResetCRCLeftPanel';
import { useOutageMapOptions } from '../../providers/OutageMapOptionsProvider';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: '#fff',
      '& .MuiTypography-body2': {
        fontSize: 15,
      },
    },
    section: {
      display: 'flex',
      flexDirection: 'column',
      padding: theme.spacing(2.5, 2.5, 3.75, 2.5),
      borderBottom: `1px solid ${colors.grey2}`,
    },
    expandedSection: {
      gap: theme.spacing(2),
      padding: theme.spacing(3.75, 2.5, 2.5, 2.5),
    },
    collapsedSection: {
      padding: theme.spacing(2, 1),
      textAlign: 'center',
    },
    expandedIcon: {
      width: 40,
      height: 40,
    },
    collapsedIcon: {
      width: 36,
      height: 35,
    },
    areaIconExpanded: {
      width: 32,
      height: 40,
    },
    areaIconCollapsed: {
      width: 30,
      height: 30,
    },
    markerLabel: {
      fontWeight: 600,
    },
    row: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
    },
    greyBox: {
      backgroundColor: '#f5f5f5',
      borderRadius: 10,
      padding: theme.spacing(2),
      marginTop: theme.spacing(2),
    },
    divider: {
      margin: theme.spacing(0, 2.5),
    },
    fontBold: {
      fontWeight: 600,
    },
    statusLabel: {
      width: '55%',
    },
    statusValue: {
      width: '45%',
    },
    restorationStatus: {
      cursor: 'pointer',
      textDecoration: 'underline',
      color: colors.sparkBlue,
    },
    closeIcon: {
      position: 'absolute',
      right: 18,
      marginTop: 4,
      color: colors.noirBlur,
      fontSize: 32,
    },
    backSection: {
      padding: theme.spacing(1.25, 2.5),
      cursor: 'pointer',
    },
    bold: {
      fontWeight: 600,
    },
  }),
);

interface MarkerIconConfig {
  icon: React.ReactNode;
  label: string;
}

interface MarkerConfig {
  expanded: MarkerIconConfig;
  collapsed: MarkerIconConfig;
}

interface MarkerContentProps {
  expanded: boolean;
  markerConfig: MarkerConfig;
  t: (slug: string) => string;
  classes: ReturnType<typeof useStyles>;
}

const MarkerContent: React.FC<MarkerContentProps> = ({
  expanded,
  markerConfig,
  t,
  classes,
}) => {
  return expanded ? (
    <Box display="flex" alignItems="center" gridGap={10}>
      {markerConfig.expanded.icon}
      <Typography variant="body1" className={classes.markerLabel}>
        {t(markerConfig.expanded.label)}
      </Typography>
    </Box>
  ) : (
    <Box display="flex" flexDirection="column" gridGap={8} textAlign="center">
      <div>{markerConfig.collapsed.icon}</div>
      <Typography variant="caption" className={classes.markerLabel}>
        {t(markerConfig.collapsed.label)}
      </Typography>
    </Box>
  );
};

export const ReportedOutageInfo: React.FC = () => {
  const classes = useStyles();
  const {
    panelOpen: expanded,
    outageMarkerView,
    setOutageMarkerView,
    selectedOutageInfo,
    setSelectedOutageInfo,
    selectedAccountForOutage,
    crcMarkerDetails,
    multiPremiseInfo,
    singlePremiseInfo,
    setSinglePremiseInfo,
    setMultiPremiseInfo,
    setSelectedAccountForOutage,
  } = useOutageLeftPanelState();
  const { t } = useTranslation();
  const [
    openRestorationStatusFAQ,
    setOpenRestorationStatusFAQ,
  ] = React.useState(false);
  const { resetView } = useOutageMapContext();
  const { isAuthenticated } = useAuth();
  const isMobile = useIsMobile();
  const { outageToggles } = useOutageMapOptions();
  const {
    estimatedTimeOn,
    crewDispatchStatus,
    customersAffected,
    estimatedTimeOut,
    cause,
    eventStatus,
  } =
    multiPremiseInfo?.selectedPremiseDetails?.outages?.[0] ||
    (singlePremiseInfo?.premiseDetails as ServicePointOutageDetails) ||
    selectedOutageInfo ||
    {};
  if (!outageMarkerView) {
    return null;
  }

  const markerConfigs: Record<OutageMarkerView, MarkerConfig> = {
    [OutageMarkerView.CustomerAffected]: {
      expanded: {
        icon: (
          <OutageMarkerIcon
            text={selectedOutageInfo?.totalCustomersImpacted || 1}
            fillColor="white"
            size={40}
            textColor={colors.roseRed}
            className={classes.expandedIcon}
          />
        ),
        label: 'OUTAGE_REPORTED',
      },
      collapsed: {
        icon: (
          <OutageMarkerIcon
            text={selectedOutageInfo?.totalCustomersImpacted || 1}
            fillColor="white"
            size={36}
            textColor={colors.roseRed}
            className={classes.collapsedIcon}
          />
        ),
        label: 'EVENT',
      },
    },
    [OutageMarkerView.Grouped]: {
      expanded: {
        icon: (
          <OutageMarkerIcon
            text={selectedOutageInfo?.totalCustomersImpacted || 1}
            variant="square"
            fillColor="white"
            size={40}
            textColor={colors.roseRed}
            className={classes.expandedIcon}
          />
        ),
        label: 'OUTAGE_REPORTED',
      },
      collapsed: {
        icon: (
          <OutageMarkerIcon
            text={selectedOutageInfo?.totalCustomersImpacted || 1}
            variant="square"
            fillColor="white"
            size={36}
            textColor={colors.roseRed}
            className={classes.collapsedIcon}
          />
        ),
        label: 'EVENT',
      },
    },
    [OutageMarkerView.NoOutage]: {
      expanded: {
        icon: <HomePinIcon className={classes.expandedIcon} />,
        label: 'NO_OUTAGE_REPORTED',
      },
      collapsed: {
        icon: <HomePinIcon className={classes.collapsedIcon} />,
        label: 'LOCATION',
      },
    },
    [OutageMarkerView.SearchedLocation]: {
      expanded: {
        icon: (
          <HomePinIcon
            color={colors.roseRed}
            className={classes.expandedIcon}
          />
        ),
        label: 'OUTAGE_REPORTED',
      },
      collapsed: {
        icon: <HomePinIcon color="white" className={classes.collapsedIcon} />,
        label: 'LOCATION',
      },
    },
    [OutageMarkerView.Planned]: {
      expanded: {
        icon: <PlannedOutageIcon className={classes.expandedIcon} />,
        label: 'PLANNED_OUTAGES',
      },
      collapsed: {
        icon: <PlannedOutageIcon className={classes.collapsedIcon} />,
        label: 'PLANNED',
      },
    },
    [OutageMarkerView.CRC]: {
      expanded: {
        icon: <CRCIcon />,
        label: 'COMMUNITY_RESOURCE_CENTER',
      },
      collapsed: {
        icon: <CRCIcon className={classes.collapsedIcon} />,
        label: 'CRC',
      },
    },
    [OutageMarkerView.PSPS]: {
      expanded: {
        icon: null,
        label: '',
      },
      collapsed: {
        icon: null,
        label: '',
      },
    },
  };

  const markerConfig = markerConfigs[outageMarkerView];

  const handleClose = () => {
    setOutageMarkerView(undefined);
    setSelectedOutageInfo(undefined);
    setSinglePremiseInfo(undefined);
    setMultiPremiseInfo(undefined);
    setSelectedAccountForOutage(
      selectedAccountForOutage ? { ...selectedAccountForOutage } : undefined,
    );
    if (!isAuthenticated || !selectedAccountForOutage) {
      resetView();
    }
  };

  const { title, description } = getRestorationStatus(eventStatus || '');
  const infoBoxContent =
    outageMarkerView === OutageMarkerView.NoOutage
      ? t('NO_OUTAGE_REPORTED_STATUS')
      : description;

  return (
    <Box className={classes.container}>
      {expanded && !isMobile && (
        <Box
          display={'flex'}
          alignItems={'center'}
          className={classes.backSection}
          gridGap={10}
          borderTop={
            outageMarkerView === OutageMarkerView.NoOutage ||
            outageMarkerView === OutageMarkerView.SearchedLocation
              ? `1px solid ${colors.grey2}`
              : ''
          }
          borderBottom={`1px solid ${colors.grey2}`}
          onClick={() => handleClose()}
        >
          <ChevronLeft />
          <Typography variant="body2" className={classes.bold}>
            {t('BACK')}
          </Typography>
        </Box>
      )}
      {outageMarkerView !== OutageMarkerView.PSPS && (
        <Box
          className={
            expanded ? classes.expandedSection : classes.collapsedSection
          }
        >
          <MarkerContent
            expanded={expanded}
            markerConfig={markerConfig}
            t={t}
            classes={classes}
          />
        </Box>
      )}
      {outageMarkerView === OutageMarkerView.CRC && outageToggles.psps && (
        <ResetCRCLeftPanel />
      )}

      {outageMarkerView === OutageMarkerView.PSPS && (
        <>
          {expanded && <Divider />}
          <PSPSInfo />
        </>
      )}

      {expanded && outageMarkerView !== OutageMarkerView.PSPS && (
        <>
          <Divider className={classes.divider} />

          <Box className={classes.section}>
            {isAuthenticated && outageMarkerView === OutageMarkerView.Grouped && (
              <Typography variant="body2" style={{ marginBottom: 10 }}>
                {t('OUTAGES_AT_MULTIPLE_LOCATIONS')}
              </Typography>
            )}

            {outageMarkerView === OutageMarkerView.Planned && (
              <Box mb={2}>
                <Typography variant="body2">
                  {t('SCHEDULED_AN_OUTAGE')}
                </Typography>
              </Box>
            )}

            {(outageMarkerView === OutageMarkerView.SearchedLocation ||
              outageMarkerView === OutageMarkerView.NoOutage ||
              outageMarkerView === OutageMarkerView.CRC) && (
              <Box display="flex" gridGap={8} mb={2}>
                <Typography variant="body2" style={{ width: '30%' }}>
                  {t('LOCATION')}:
                </Typography>
                <Typography variant="body2" className={classes.fontBold}>
                  {outageMarkerView === OutageMarkerView.CRC
                    ? crcMarkerDetails?.address
                    : `${singlePremiseInfo?.houseNumber ||
                        multiPremiseInfo?.selectedPremiseDetails
                          ?.houseNumber}...`}
                </Typography>
              </Box>
            )}

            {outageMarkerView === OutageMarkerView.CRC && (
              <Typography variant="body2">
                {t('VISIT_CRC_TO_GET_LATEST_INFO')}
              </Typography>
            )}

            {outageMarkerView !== OutageMarkerView.NoOutage &&
              outageMarkerView !== OutageMarkerView.CRC && (
                <Box display="flex" flexDirection="column" gridGap={8}>
                  {outageMarkerView !== OutageMarkerView.Planned && (
                    <Box display="flex">
                      <Typography
                        variant="body2"
                        className={classes.statusLabel}
                      >
                        {t('ESTIMATED_RESTORATION_NEW')}:
                      </Typography>
                      <Typography
                        variant="body2"
                        className={
                          estimatedTimeOn
                            ? clsx(classes.fontBold, classes.statusValue)
                            : classes.statusValue
                        }
                      >
                        {estimatedTimeOn
                          ? toDateString(estimatedTimeOn, 'MMM d, h:mm a')
                              .replace(/AM/gi, 'a.m.')
                              .replace(/PM/gi, 'p.m.')
                          : t('TO_BE_DETERMINED')}
                      </Typography>
                    </Box>
                  )}

                  {outageMarkerView !== OutageMarkerView.Planned && (
                    <Box display="flex">
                      <Typography
                        variant="body2"
                        className={classes.statusLabel}
                      >
                        {t('RESTORATION_STATUS_NEW')}:
                      </Typography>

                      <Typography
                        variant="body2"
                        className={clsx(
                          classes.fontBold,
                          classes.statusValue,
                          classes.restorationStatus,
                        )}
                        onClick={() => setOpenRestorationStatusFAQ(true)}
                      >
                        {title}
                      </Typography>
                    </Box>
                  )}

                  {outageMarkerView === OutageMarkerView.Planned && (
                    <Box display="flex">
                      <Typography
                        variant="body2"
                        className={classes.statusLabel}
                      >
                        {t('PLANNED_WORK_DATE')}:
                      </Typography>
                      <Typography
                        variant="body2"
                        className={classes.statusValue}
                      >
                        {estimatedTimeOut
                          ? toDateString(estimatedTimeOut, 'MMM d')
                          : t('TO_BE_DETERMINED')}
                      </Typography>
                    </Box>
                  )}

                  {(outageMarkerView === OutageMarkerView.SearchedLocation ||
                    outageMarkerView === OutageMarkerView.Planned) && (
                    <Box display="flex">
                      <Typography
                        variant="body2"
                        className={classes.statusLabel}
                      >
                        {t('CUSTOMERS_AFFECTED')}:
                      </Typography>
                      <Typography
                        variant="body2"
                        className={classes.statusValue}
                      >
                        {selectedOutageInfo
                          ? selectedOutageInfo?.totalCustomersImpacted
                          : customersAffected}
                      </Typography>
                    </Box>
                  )}

                  {outageMarkerView !== OutageMarkerView.Planned && (
                    <Box display="flex">
                      <Typography
                        variant="body2"
                        className={classes.statusLabel}
                      >
                        {t('OUTAGE_IDENTIFIED')}:
                      </Typography>
                      <Typography
                        variant="body2"
                        className={classes.statusValue}
                      >
                        {estimatedTimeOut
                          ? toDateString(estimatedTimeOut, 'MMM d, h:mm a')
                              .replace(/AM/gi, 'a.m.')
                              .replace(/PM/gi, 'p.m.')
                          : t('TO_BE_DETERMINED')}
                      </Typography>
                    </Box>
                  )}

                  {cause &&
                    outageMarkerView !== OutageMarkerView.Planned &&
                    cause !== 'Under Investigation' && (
                      <Box display="flex">
                        <Typography
                          variant="body2"
                          className={classes.statusLabel}
                        >
                          {t('CAUSE_OF_OUTAGE')}:
                        </Typography>
                        <Typography
                          variant="body2"
                          className={classes.statusValue}
                        >
                          {cause}
                        </Typography>
                      </Box>
                    )}
                </Box>
              )}

            {outageMarkerView !== OutageMarkerView.CRC &&
              outageMarkerView !== OutageMarkerView.Planned &&
              infoBoxContent && (
                <Box className={classes.greyBox}>
                  <Typography variant="body2">{infoBoxContent}</Typography>
                </Box>
              )}
          </Box>
        </>
      )}
      <RestorationStatusFAQ
        open={openRestorationStatusFAQ}
        setOpen={setOpenRestorationStatusFAQ}
      />
    </Box>
  );
};
